import React from 'react';
import {
  Grid,
  TextField,
  Box,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import type { Weekday } from '@musedrops/shared';
import { ConfigItem, AppConfig } from '../../../services/configApi';
import { SectionSaveButtons } from './SectionSaveButtons';
import { useSectionConfig } from '../hooks/useSectionConfig';

interface SchedulingConfigProps {
  config: AppConfig;
  items: ConfigItem[];
  disabled?: boolean;
}

const SCHEDULING_KEYS: (keyof AppConfig)[] = [
  'default_publish_weekdays',
  'publish_hour',
];

const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export const SchedulingConfig: React.FC<SchedulingConfigProps> = ({
  config,
  items,
  disabled = false,
}) => {
  const {
    currentValues,
    hasChanges,
    saving,
    updateValue,
    saveChanges,
    discardChanges,
  } = useSectionConfig(SCHEDULING_KEYS, config);

  const [notification, setNotification] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // State for selected weekdays
  const [selectedWeekdays, setSelectedWeekdays] = React.useState<Set<Weekday>>(
    new Set()
  );

  // Parse weekdays from config when it changes
  React.useEffect(() => {
    const weekdaysStr = currentValues.default_publish_weekdays as string | null;
    if (weekdaysStr) {
      const weekdaysArray = weekdaysStr
        .split(',')
        .map(d => d.trim() as Weekday)
        .filter(d => d); // Remove empty strings
      setSelectedWeekdays(new Set(weekdaysArray));
    } else {
      // If no custom weekdays, start with empty set
      setSelectedWeekdays(new Set());
    }
  }, [currentValues.default_publish_weekdays]);

  // Get config item details for validation and description
  const getConfigItem = (key: string) => {
    return items.find(item => item.key === key);
  };

  const getValidationError = (key: string, value: number): string | null => {
    const item = getConfigItem(key);
    if (!item?.validation_rules) return null;

    const { min, max } = item.validation_rules;
    if (min !== undefined && value < min) {
      return `Must be at least ${min}`;
    }
    if (max !== undefined && value > max) {
      return `Must be at most ${max}`;
    }
    return null;
  };

  // Handle weekday checkbox toggle
  const handleWeekdayToggle = (day: Weekday) => {
    const newSelected = new Set(selectedWeekdays);
    if (newSelected.has(day)) {
      newSelected.delete(day);
    } else {
      newSelected.add(day);
    }
    setSelectedWeekdays(newSelected);

    // Update config value - convert Set to comma-separated string
    if (newSelected.size === 0) {
      updateValue('default_publish_weekdays', null);
    } else {
      // Maintain consistent day ordering
      const sortedDays: Weekday[] = [
        'sun',
        'mon',
        'tue',
        'wed',
        'thu',
        'fri',
        'sat',
      ];
      const orderedDays = sortedDays.filter(d => newSelected.has(d));
      updateValue('default_publish_weekdays', orderedDays.join(','));
    }
  };

  const handleHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      updateValue('publish_hour', value);
    }
  };

  const handleSave = async () => {
    const result = await saveChanges();
    setNotification({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
  };

  const handleDiscard = () => {
    discardChanges();
    setNotification({
      message: 'Changes discarded',
      type: 'success',
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const hour = Number(currentValues.publish_hour) || 0;
  const hourError = getValidationError('publish_hour', hour);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Publish Days */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {WEEKDAY_OPTIONS.map(({ value, label }) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    checked={selectedWeekdays.has(value)}
                    onChange={() => handleWeekdayToggle(value)}
                    disabled={disabled || saving}
                  />
                }
                label={label}
                sx={{ mr: 1 }}
              />
            ))}
          </Box>
        </Grid>

        {/* Publish Hour */}
        <Grid item xs={6} sm={4} md={3}>
          <TextField
            label="Hour (UTC)"
            type="number"
            value={hour}
            onChange={handleHourChange}
            disabled={disabled || saving}
            error={!!hourError}
            helperText={hourError}
            fullWidth
            sx={{ minWidth: '180px' }}
            inputProps={{
              min: 0,
              max: 23,
            }}
          />
        </Grid>
      </Grid>

      <SectionSaveButtons
        hasChanges={hasChanges}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        disabled={disabled}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={closeNotification}
      >
        <Alert
          onClose={closeNotification}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
